import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import DeliveryBoyForm from '../components/DeliveryBoyForm';
import { getDeliveryBoyById, updateDeliveryBoy } from '../api/index';
import { DeliveryBoy, DeliveryBoyFormData } from '../types';
import { FiArrowLeft, FiSave, FiLoader } from 'react-icons/fi';

const EditDeliveryBoy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deliveryBoy, setDeliveryBoy] = useState<DeliveryBoy | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDeliveryBoy = async () => {
      if (!id) {
        setError('Invalid delivery boy ID');
        setFetchLoading(false);
        return;
      }

      try {
        const boy = await getDeliveryBoyById(parseInt(id));
        if (boy) {
          setDeliveryBoy(boy);
        } else {
          setError('Delivery boy not found');
        }
      } catch (err) {
        console.error('Error fetching delivery boy:', err);
        setError('Failed to load delivery boy details');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchDeliveryBoy();
  }, [id]);

  const handleSubmit = async (formData: DeliveryBoyFormData): Promise<void> => {
    if (!deliveryBoy) return;

    setLoading(true);
    setSuccessMessage('');
    setError('');
    
    try {
      const updatedDeliveryBoy = await updateDeliveryBoy(deliveryBoy.id, formData);
      setDeliveryBoy(updatedDeliveryBoy);
      setSuccessMessage(`Delivery boy ${updatedDeliveryBoy.firstName} ${updatedDeliveryBoy.lastName} has been updated successfully!`);
      
      // Navigate to list after a brief delay to show success message
      setTimeout(() => {
        navigate('/admin/delivery-boys');
      }, 2000);
    } catch (error) {
      console.error('Error updating delivery boy:', error);
      setError('Failed to update delivery boy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/delivery-boys');
  };

  if (fetchLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2 text-slate-500 dark:text-slate-400">
            <FiLoader className="h-6 w-6 animate-spin" />
            <span>{t('action.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !deliveryBoy) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/delivery-boys')}
              className="flex items-center space-x-2"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back to List</span>
            </Button>
          </div>
        </div>

        <Card className="border-red-200 bg-red-50">
          <div className="p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-700">{error || 'Delivery boy not found'}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/delivery-boys')}
              className="mt-4"
            >
              Return to List
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/delivery-boys')}
            className="flex items-center space-x-2"
          >
            <FiArrowLeft className="h-4 w-4" />
            <span>Back to List</span>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              {t('action.edit')}: {deliveryBoy.firstName} {deliveryBoy.lastName}
            </h1>
            <p className="text-slate-600 dark:text-slate-300">
              {deliveryBoy.employeeId}
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Card className="border-green-200 bg-green-50">
          <div className="flex items-center space-x-2 text-green-800 p-4">
            <FiSave className="h-5 w-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4 text-red-800">
            <span className="font-medium">{error}</span>
          </div>
        </Card>
      )}

      {/* Form */}
      <Card>
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Update Delivery Boy Information</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Modify the information below to update the delivery boy profile.
            </p>
          </div>

          <DeliveryBoyForm
            deliveryBoy={deliveryBoy}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      </Card>

      {/* Help Text */}
      <Card className="border-blue-200 bg-blue-50">
        <div className="p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Update Guidelines:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Changes to personal information may require document verification</li>
            <li>• Vehicle type changes must be accompanied by valid license documents</li>
            <li>• Salary changes will be effective from the next payroll cycle</li>
            <li>• Status changes will immediately affect delivery availability</li>
            <li>• Document updates require admin approval</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default EditDeliveryBoy;